import type { ReactNode, FC } from 'react';
import React, { useCallback } from 'react';

import { token } from '@atlaskit/tokens';
import { B50, N20, N800 } from '@atlaskit/theme/colors';
import { LinkItem } from '@atlaskit/menu';

import { HOME } from '@confluence/named-routes';

import { useSelectedView } from '../useSelectedView';
import { View } from '../homeRouteParams';

import { useHomeNavigationAnalytics } from './useHomeNavigationAnalytics';

const getCSS = (state) => {
	const { isSelected } = state;
	const color = `${
		isSelected ? token('color.text.selected', '#0C66E4') : token('color.text', N800)
	} !important`;
	const backgroundColor = `${isSelected ? token('color.background.selected', B50) : 'transparent'}`;
	const hoverBackgroundColor = `${
		isSelected
			? token('color.background.selected.hovered', '#CCE0FF')
			: token('color.background.neutral.subtle.hovered', N20)
	}`;

	return {
		'& [data-item-elem-before]': {
			width: '24px',
			marginRight: 'calc((12px - 12px * var(--homeNavIsCollapsed)) * var( --homeIsLegacy))',
		},
		'& [data-item-title]': {
			// skeleton width must match: next/packages/home/src/Home/HomeNavigation/index.ts
			width: '190px',
			display: 'var(--homeNavCollapsedDisplay)',
		},
		'& > div': {
			columnGap: 'calc(12px - 12px * var(--homeNavIsCollapsed))',
		},

		'& > span': {
			gap: 'calc(12px - 12px * var(--homeNavIsCollapsed))',
		},

		backgroundColor,
		borderRadius: 'calc(3px - 3px * var(--homeIsLegacy))',
		padding: '8px calc(8px + 8px * var(--homeIsLegacy))',
		textDecoration: 'none',
		color,
		'&:visited': {
			color,
			textDecoration: 'none',
		},
		'&:active': {
			color,
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
		},
		'&:hover': {
			backgroundColor: hoverBackgroundColor,
			textDecoration: 'none',
			color,
		},
		paddingBlock: '0px',
		minHeight: '36px',

		...(isSelected && {
			'::before': {
				content: '""',
				position: 'absolute',
				top: '50%',
				left: 0,
				width: '4px',
				height: '16px',
				transform: 'translateY(-50%)',
				backgroundColor: token('color.border.selected', '#0C66E4'),
				borderRadius: `0 ${token('border.radius', '4px')} ${token('border.radius', '4px')} 0`,
			},
		}),
	};
};

type HrefItemProps = {
	href?: string;
	view?: never;
};

type ViewItemProps = {
	view: View;
	href?: never;
};

type HomeNavigationRenderProps = {
	isSelected: boolean;
};

type HomeNavigationItemProps = {
	icon: ReactNode;
	onClick?: () => void;
	render?: (props: HomeNavigationRenderProps) => ReactNode;
	children?: React.ReactNode;
} & (ViewItemProps | HrefItemProps);

const getHref = (view: View) => {
	if (view === View.OVERVIEW) {
		return HOME.toUrl();
	}
	return HOME.toUrl({ view });
};

export const HomeNavigationItem: FC<HomeNavigationItemProps> = ({
	view,
	href,
	icon,
	onClick,
	render,
	children,
}) => {
	const selectedView = useSelectedView();
	const fireAnalytics = useHomeNavigationAnalytics();

	const onLinkClick = useCallback(() => {
		view && fireAnalytics(view);
		onClick?.();
	}, [view, fireAnalytics, onClick]);

	return (
		<LinkItem
			onClick={onLinkClick}
			// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
			cssFn={getCSS}
			iconBefore={icon}
			isSelected={view && selectedView === view}
			href={href || (view && getHref(view))}
			target={href && '_blank'}
		>
			{render ? render({ isSelected: selectedView === view }) : children}
		</LinkItem>
	);
};
